import styles from './newCompanyModal.module.scss';
import Modal from '@mui/material/Modal';
import Fade from '@mui/material/Fade';
import CloseIcon from '@mui/icons-material/Close';
import IconButton from '@mui/material/IconButton';
import { Button, CircularProgress, TextField } from '@mui/material';
import { useState } from 'react';
import { useValidator } from 'hooks/useValidator';
import first from 'lodash/first';
import { fetchCompanies } from 'api/companies';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { useTranslation } from 'react-i18next';

const emptyDuplicatedCompanyValidation = { message: '', valid: true };

export default function NewCompanyModal(props) {
  const [company, setCompany] = useState(props.input || '');
  const [duplicatedCompany, setDuplicatedCompany] = useState(emptyDuplicatedCompanyValidation);
  const [validatingFields, setValidatingFields] = useState(false);
  const { t } = useTranslation();

  const resetModal = () => {
    setDuplicatedCompany(emptyDuplicatedCompanyValidation);
    validator.reset();
    setValidatingFields(false);
    setCompany('');
  };

  const handleCancel = () => {
    props.onAction({ confirm: false, value: '' });
    resetModal();
  };

  const handleCreateNewCompany = async () => {
    setValidatingFields(true);
    if (!validator.validate()) {
      setValidatingFields(false);
      return;
    }

    const isExistentCompany = await companyExists(company);
    if (isExistentCompany) {
      setDuplicatedCompany({ message: t('home.companyExists'), valid: false });
      setValidatingFields(false);
      return;
    }

    props.onAction({ confirm: true, value: company });
    resetModal();
  };

  const companyExists = async (companyName) => {
    const companies = await fetchCompanies(companyName);
    return companies.length > 0 && first(companies).name === companyName;
  };

  const [validables, validator] = useValidator(['field'], () => {
    const message = {};
    if (!company) message.field = t('home.companyNameIsRequired');
    return message;
  });

  return (
    <Modal
      data-testid="NewCompanyModal"
      aria-labelledby="transition-modal-title"
      aria-describedby="transition-modal-description"
      open={props.open}
      onClose={handleCancel}
      closeAfterTransition
      BackdropProps={{
        timeout: 500,
      }}>
      <Fade data-testid="NewCompanyFade" in={props.open}>
        <div className={styles.content}>
          <div className={styles.main}>
            <div className={styles.title}>{t('home.createNew')}</div>
            <IconButton
              data-testid="NewCompanyCloseBtn"
              className={styles.close_button}
              disabled={validatingFields}
              onClick={handleCancel}>
              <CloseIcon fontSize={'large'} />
            </IconButton>
          </div>
          <div className={styles.fields}>
            <TextField
              data-testid="NewCompanyTextField"
              className={styles.field}
              autoFocus
              required
              label={t('home.company')}
              value={company}
              error={!validables.field.valid || !duplicatedCompany.valid}
              helperText={validables.field.message || duplicatedCompany.message}
              onChange={(event) => {
                setDuplicatedCompany(emptyDuplicatedCompanyValidation);
                validator.reset();
                setCompany(event.target.value);
              }}
            />
          </div>
          <div className={styles.buttons}>
            <Button
              data-testid="NewCompanyCloseBtn2"
              className={styles.buttons_cancel}
              size="small"
              variant="contained"
              startIcon={<CancelIcon />}
              disabled={validatingFields}
              onClick={handleCancel}>
              {t('common.cancel')}
            </Button>
            <Button
              data-testid="NewCompanyCreateBtn"
              className={styles.buttons_confirm}
              size="small"
              variant="contained"
              startIcon={
                validatingFields ? (
                  <CircularProgress
                    data-testid="NewCompanyLoadingSpinner"
                    size={16}
                    color={'primary'}
                  />
                ) : (
                  <AddCircleIcon />
                )
              }
              onClick={handleCreateNewCompany}
              disabled={validatingFields}
              disableRipple>
              {t('common.create')}
            </Button>
          </div>
        </div>
      </Fade>
    </Modal>
  );
}
